import { Box, Stack } from '@mui/material';
import { FC, ReactNode, RefObject, useCallback, useEffect, useMemo, useRef } from 'react';
import { createUseStyles } from 'react-jss';
import UserBadge from '@/components/user-badge';
import useApp from '@/hooks/use-app.hook';
import ThreadMessage from './thread-message';
import useThread from '@/hooks/use-thread.hook';
import useToast from '@/hooks/use-toast.hook';
import { ShortcutMessage } from '@/lib/models/shortcut.model';
import Icon from '@/components/icon';
import { MessageControlInstance } from './message-control';
import ThreadLocked from './thread-locked';
import { ThreadMessageModel } from '@/lib/models/thread/thread-message.model';
import { cloneDeep } from 'lodash';
import useScroll from '@/hooks/use-scroll.hook';
import SystemBadge from './system-badge';
import StreamThreadMessage from './stream-thread-message';
import useUserSettings from '@/hooks/use-user-settings.hook';
import { getBreakpoint } from '@/theme';

interface Props {
  messageControlRef: RefObject<MessageControlInstance>;
}

const useStyles = createUseStyles({
  threadList: {
    gap: 40,
  },
  spacer: {
    height: 1,
    minHeight: 1,
    width: '100%',
  },
  loaderControlMessage: {
    color: '#686868',
  },
  [getBreakpoint('md')]: {
    threadList: {
      gap: 20,
    },
  },
});

const ThreadList: FC<Props> = ({ messageControlRef }) => {
  const { user } = useApp();
  const {
    sendMessage,
    removeDeckMessage,
    sendDeckMessage,
    setWorking,
    thread,
    loadState,
    settings,
    status: threadStatus,
  } = useThread();

  const {
    agents,
    preferences: { tail },
  } = useUserSettings();

  const { errorToast } = useToast();
  const styles = useStyles();

  const { conversation, context: threadContext, locked } = thread;
  const { agentId } = settings;

  const { jumpToBottom, scrollToBottom, startTail, stopTail } = useScroll();

  const handleRegenerate = useCallback(
    (message: ThreadMessageModel) => {
      const { conversation = [], locked } = thread;

      if (locked) {
        return;
      }

      const messageIndex = conversation.findIndex(({ id }) => id === message.id);
      let priorUserMessage: ThreadMessageModel | null = null;

      if (messageIndex) {
        const priorMessage = conversation[messageIndex - 1];
        if (priorMessage?.role === 'user') {
          priorUserMessage = priorMessage;
        }
      }

      if (!priorUserMessage) {
        errorToast('Unable to find message');
        return;
      }

      const { content, shortcut } = priorUserMessage;

      sendMessage(content, shortcut);
    },
    [thread, errorToast, sendMessage]
  );

  const handleSendShortcut = useCallback(
    (message: ThreadMessageModel, shortcut: ShortcutMessage) => {
      if (thread.locked) {
        return;
      }
      const shortcutMessage = cloneDeep(message);
      shortcutMessage.shortcut = shortcut;
      sendDeckMessage(shortcutMessage);
    },
    [sendDeckMessage, thread.locked]
  );

  const handleRemoveShortcut = useCallback(
    (messageId: string) => {
      removeDeckMessage(messageId);
    },
    [removeDeckMessage]
  );

  // if the onChange handler of any of the shortcut form inputs was called, the form is dirty and
  // should not be replaced by another shortcut
  const handleChangeShortcut = () => {
    setWorking(true);
  };

  const handleEdit = useCallback(
    (message: ThreadMessageModel) => {
      if (thread.locked) {
        return;
      }

      const { content, shortcut } = message;

      if (shortcut) {
        // @todo reload shortcut
      }

      messageControlRef.current?.loadContent(content);
    },
    [messageControlRef, thread.locked]
  );

  // tailing behavior
  useEffect(() => {
    // open -> send -> stream -> open
    if (threadStatus === 'open') {
      setTimeout(() => {
        stopTail();
      }, 100);
      return;
    }

    if (!tail) {
      scrollToBottom();
      return;
    }

    startTail();
  }, [threadStatus, startTail, stopTail, scrollToBottom, tail]);

  useEffect(() => {
    if (loadState === 'loaded') {
      jumpToBottom();
    }
  }, [jumpToBottom, loadState]);

  const sentOnce = useRef(false);

  const showStreamMessage = useMemo(() => {
    const sending = ['stream', 'send'].includes(threadStatus);
    if (sending) {
      sentOnce.current = true;
    }
    return sending;
  }, [threadStatus]);

  const { BadgeEl: StreamThreadBadgeEl, title: streamThreadRoleTitle } = useMemo(
    () => SystemBadge(agents, agentId),
    [agents, agentId]
  );

  return (
    <Stack width="100%" height="100%" className={styles.threadList}>
      {conversation.map((message, index) => {
        const { id, role, shortcut, agentId } = message;

        const isSystemMessage = role === 'system';
        const isLastMessage = index === conversation.length - 1;
        const fullHeight =
          !showStreamMessage && isLastMessage && isSystemMessage && sentOnce.current;

        let BadgeEl: ReactNode = null;
        let roleTitle = '';

        if (role === 'user') {
          BadgeEl = <UserBadge user={user!} />;
          roleTitle = 'You';

          if (shortcut) {
            BadgeEl = <Icon name="shortcut" size="large" />;
            roleTitle = `Shortcut: ${shortcut.name}`;
          }
        } else {
          const { BadgeEl: SystemBadgeEl, title: SystemTitle } = SystemBadge(agents, agentId);
          BadgeEl = SystemBadgeEl;
          roleTitle = SystemTitle;
        }

        return (
          <ThreadMessage
            key={`message-${id}`}
            BadgeEl={BadgeEl}
            message={message}
            roleTitle={roleTitle}
            onRegenerate={handleRegenerate}
            onSendShortcut={(shortcut) => handleSendShortcut(message, shortcut)}
            onChangeShortcut={handleChangeShortcut}
            onRemove={() => handleRemoveShortcut(id)}
            onEdit={handleEdit}
            threadContext={threadContext}
            threadLocked={locked}
            fullHeight={fullHeight}
          />
        );
      })}
      {showStreamMessage && (
        <StreamThreadMessage BadgeEl={StreamThreadBadgeEl} roleTitle={streamThreadRoleTitle} />
      )}
      {locked && <ThreadLocked />}
      <Box className={styles.spacer}></Box>
    </Stack>
  );
};

export default ThreadList;
